import {
  AccountInviteCompanyType,
  AccountType,
  getV1CompaniesCompanyIdAccounts,
  patchV1CompaniesCompanyIdAccountsId,
  postV1CompaniesCompanyIdAccountsInvite,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Account } from '~hooks/useAccount/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { toE164PhoneNumber } from '~utils/utilFunctions';

import { getPhoneValidationErrors } from './phoneValidationErrors';
import { EntityCSVConfig } from './types';

const VALID_COMPANY_TYPES = [
  'BROKER',
  'CONTRACTOR',
  'HAULER',
  'OWNER_OPERATOR',
  'PRODUCER',
];

// Maps the uppercase CSV company_type value to the AccountInviteCompanyType enum
// member the API expects (Account_Invite.company_type is lowercase, e.g. 'hauler').
const ACCOUNT_INVITE_COMPANY_TYPE_BY_CSV_VALUE: Record<string, AccountInviteCompanyType> =
  {
    BROKER: AccountInviteCompanyType.BROKER,
    CONTRACTOR: AccountInviteCompanyType.CONTRACTOR,
    HAULER: AccountInviteCompanyType.HAULER,
    OWNER_OPERATOR: AccountInviteCompanyType.OWNER_OPERATOR,
    PRODUCER: AccountInviteCompanyType.PRODUCER,
  };

const vendorsColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Acme Trucking',
    description: 'Vendor/company name',
  },
  {
    key: 'company_type',
    header: 'company_type',
    required: true,
    enumValues: VALID_COMPANY_TYPES,
    exampleValue: 'HAULER',
    description: 'Required: BROKER, CONTRACTOR, HAULER, OWNER_OPERATOR, or PRODUCER',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'VND-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'department',
    header: 'department',
    required: false,
    exampleValue: '',
    description: 'Department (must exist in system)',
  },
  {
    key: 'accounting_id',
    header: 'accounting_id',
    required: false,
    exampleValue: '',
    description: 'Accounting system reference',
  },
  {
    key: 'primary_contact_first_name',
    header: 'primary_contact_first_name',
    required: true,
    exampleValue: 'John',
    description: 'Required: Primary contact first name',
  },
  {
    key: 'primary_contact_last_name',
    header: 'primary_contact_last_name',
    required: true,
    exampleValue: 'Doe',
    description: 'Required: Primary contact last name',
  },
  {
    key: 'primary_contact_email',
    header: 'primary_contact_email',
    required: false,
    exampleValue: 'john@example.com',
    description: 'Required if no phone provided',
  },
  {
    key: 'primary_contact_phone',
    header: 'primary_contact_phone',
    required: false,
    exampleValue: '+14155551234',
    description:
      'US numbers like 555-555-5555 or (555) 555-5555; other countries start with + and the country code (e.g., +14155551234) — required if no email',
  },
  {
    key: 'address_line_1',
    header: 'address_line_1',
    required: false,
    exampleValue: '123 Main St',
    description: 'Street address',
  },
  {
    key: 'address_city',
    header: 'address_city',
    required: false,
    exampleValue: 'Austin',
    description: 'City',
  },
  {
    key: 'address_state',
    header: 'address_state',
    required: false,
    exampleValue: 'TX',
    description: '2-letter state code',
  },
  {
    key: 'address_zip',
    header: 'address_zip',
    required: false,
    exampleValue: '78701',
    description: 'ZIP code',
  },
  {
    key: 'truck_count',
    header: 'truck_count',
    required: false,
    exampleValue: '10',
    description: 'Number of trucks',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Additional notes (max 350 chars)',
  },
];

export const createVendorsConfig = (
  getDepartmentIdByName: (name: string) => string | undefined,
): EntityCSVConfig<Account> => ({
  entityType: 'vendors',
  columns: vendorsColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdAccounts({
          path: { 'company-id': companyId },
          query: { ...query, 'filter[account_types]': [AccountType.VENDOR] },
        }),
      Account.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<Account> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    // company_type expects the AccountInviteCompanyType enum member matching the
    // CSV's uppercase value (e.g. 'HAULER' -> AccountInviteCompanyType.HAULER)
    const companyType = data.company_type
      ? ACCOUNT_INVITE_COMPANY_TYPE_BY_CSV_VALUE[data.company_type.toUpperCase()]
      : undefined;

    const response = await postV1CompaniesCompanyIdAccountsInvite({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        account_types: [AccountType.VENDOR], // Always VENDOR - no need to read from CSV
        company_type: companyType,
        external_id: data.external_id || null,
        accounting_id: data.accounting_id || null,
        department_id: departmentId || null,
        notes: data.notes || null,
        contact: {
          name: `${data.primary_contact_first_name} ${data.primary_contact_last_name}`,
          email: data.primary_contact_email || undefined,
          phone: toE164PhoneNumber(data.primary_contact_phone),
        },
        address: {
          thoroughfare: data.address_line_1 || '',
          locality: data.address_city || '',
          administrative_area: data.address_state || '',
          postal_code: data.address_zip || '',
          country: 'US',
        },
        auto_accept_orders: false,
      },
    });

    return Account.parse(response.data.data);
  },

  update: async (
    id: string,
    data: Record<string, string>,
    companyId: string,
  ): Promise<Account> => {
    const departmentId = data.department
      ? getDepartmentIdByName(data.department)
      : undefined;

    const primaryContactName =
      data.primary_contact_first_name && data.primary_contact_last_name
        ? `${data.primary_contact_first_name} ${data.primary_contact_last_name}`
        : undefined;

    const response = await patchV1CompaniesCompanyIdAccountsId({
      path: { 'company-id': companyId, id },
      body: {
        name: data.name,
        account_types: [AccountType.VENDOR], // Always VENDOR
        external_id: data.external_id || null,
        accounting_id: data.accounting_id || null,
        department_id: departmentId || null,
        notes: data.notes || null,
        primary_contact: primaryContactName
          ? {
              name: primaryContactName,
              email: data.primary_contact_email || undefined,
              phone: toE164PhoneNumber(data.primary_contact_phone),
            }
          : undefined,
        address: data.address_line_1
          ? {
              thoroughfare: data.address_line_1,
              locality: data.address_city || '',
              administrative_area: data.address_state || '',
              postal_code: data.address_zip || '',
              country: 'US',
            }
          : undefined,
      },
    });

    return Account.parse(response.data.data);
  },

  transformExport: (account: Account): Record<string, string> => {
    // Split primary contact name into first/last for export
    const primaryContactNameParts = (account.primaryContact?.name || '').split(' ');
    const primaryContactFirstName = primaryContactNameParts[0] || '';
    const primaryContactLastName = primaryContactNameParts.slice(1).join(' ') || '';

    return {
      id: account.id,
      name: account.name,
      company_type: account.connectedCompany?.companyType || '',
      external_id: account.externalId || '',
      department: account.department?.name || '',
      accounting_id: account.accountingId || '',
      primary_contact_first_name: primaryContactFirstName,
      primary_contact_last_name: primaryContactLastName,
      primary_contact_email: account.primaryContact?.email || '',
      primary_contact_phone: account.primaryContact?.phone || '',
      address_line_1: account.address?.streetAddress || '',
      address_city: account.address?.locality || '',
      address_state: account.address?.administrativeArea || '',
      address_zip: account.address?.postalCode || '',
      truck_count: account.truckCount?.toString() || '',
      notes: account.notes || '',
    };
  },

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    company_type: row.company_type?.toUpperCase(),
    external_id: row.external_id?.trim() || null,
    department: row.department?.trim() || null,
    accounting_id: row.accounting_id?.trim() || null,
    notes: row.notes?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];
    const isNewRecord = !row.id?.trim();

    // Basic required fields
    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }

    // Vendor-specific requirements (for new vendors)
    if (isNewRecord) {
      // Company type required
      if (!row.company_type?.trim()) {
        errors.push(
          t('csv_import_export.errors.required_field', { field: 'company_type' }),
        );
      } else if (!VALID_COMPANY_TYPES.includes(row.company_type.toUpperCase())) {
        errors.push(
          t('csv_import_export.errors.invalid_enum', {
            field: 'company_type',
            values: VALID_COMPANY_TYPES.join(', '),
          }),
        );
      }

      // Primary contact first/last name required
      if (!row.primary_contact_first_name?.trim()) {
        errors.push(
          t('csv_import_export.errors.required_field', {
            field: 'primary_contact_first_name',
          }),
        );
      }
      if (!row.primary_contact_last_name?.trim()) {
        errors.push(
          t('csv_import_export.errors.required_field', {
            field: 'primary_contact_last_name',
          }),
        );
      }

      // Email OR phone required
      if (!row.primary_contact_email?.trim() && !row.primary_contact_phone?.trim()) {
        errors.push(t('csv_import_export.errors.email_or_phone_required'));
      }
    }

    errors.push(
      ...getPhoneValidationErrors(row.primary_contact_phone, 'primary_contact_phone'),
    );

    // Validate company_type enum if provided (even for updates)
    if (
      row.company_type?.trim() &&
      !VALID_COMPANY_TYPES.includes(row.company_type.toUpperCase())
    ) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'company_type',
          values: VALID_COMPANY_TYPES.join(', '),
        }),
      );
    }

    if (row.notes && row.notes.length > 350) {
      errors.push(t('csv_import_export.errors.number_max', { field: 'notes', max: 350 }));
    }

    return errors;
  },
});
